import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
} from '@mui/material';
import {
  Code as CodeIcon,
  WrapText as WrapTextIcon,
  FormatListNumbered as LineNumbersIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
// @ts-ignore
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
// @ts-ignore
import tsx from 'react-syntax-highlighter/dist/cjs/languages/prism/tsx';
// @ts-ignore
import typescript from 'react-syntax-highlighter/dist/cjs/languages/prism/typescript';
// @ts-ignore
import javascript from 'react-syntax-highlighter/dist/cjs/languages/prism/javascript';
// @ts-ignore
import jsx from 'react-syntax-highlighter/dist/cjs/languages/prism/jsx';
// @ts-ignore
import python from 'react-syntax-highlighter/dist/cjs/languages/prism/python';
// @ts-ignore
import java from 'react-syntax-highlighter/dist/cjs/languages/prism/java';
// @ts-ignore
import cpp from 'react-syntax-highlighter/dist/cjs/languages/prism/cpp';
// @ts-ignore
import csharp from 'react-syntax-highlighter/dist/cjs/languages/prism/csharp';
// @ts-ignore
import go from 'react-syntax-highlighter/dist/cjs/languages/prism/go';
// @ts-ignore
import rust from 'react-syntax-highlighter/dist/cjs/languages/prism/rust';
// @ts-ignore
import sql from 'react-syntax-highlighter/dist/cjs/languages/prism/sql';
// @ts-ignore
import json from 'react-syntax-highlighter/dist/cjs/languages/prism/json';
// @ts-ignore
import xml from 'react-syntax-highlighter/dist/cjs/languages/prism/xml-doc';
// @ts-ignore
import markdown from 'react-syntax-highlighter/dist/cjs/languages/prism/markdown';
// @ts-ignore
import yaml from 'react-syntax-highlighter/dist/cjs/languages/prism/yaml';
// @ts-ignore
import bash from 'react-syntax-highlighter/dist/cjs/languages/prism/bash';
// @ts-ignore
import docker from 'react-syntax-highlighter/dist/cjs/languages/prism/docker';
// @ts-ignore
import { vs, vscDarkPlus, prism, atomDark } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { ViewerError } from '../../../types/viewer.types';
import { useDirectContent } from '../../../hooks/useDirectContent';

// Register languages for better performance
SyntaxHighlighter.registerLanguage('tsx', tsx);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('jsx', jsx);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('java', java);
SyntaxHighlighter.registerLanguage('cpp', cpp);
SyntaxHighlighter.registerLanguage('csharp', csharp);
SyntaxHighlighter.registerLanguage('go', go);
SyntaxHighlighter.registerLanguage('rust', rust);
SyntaxHighlighter.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('xml', xml);
SyntaxHighlighter.registerLanguage('markdown', markdown);
SyntaxHighlighter.registerLanguage('yaml', yaml);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('docker', docker);

interface CodeViewerProps {
  contentId: string;
  metadata: {
    fileName: string;
    mimeType: string;
    fileSize: number;
    contentType: string;
  };
  onError?: (error: ViewerError) => void;
  onLoad?: () => void;
  theme?: 'light' | 'dark' | 'github' | 'vs-dark';
  showLineNumbers?: boolean;
  wordWrap?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const CodeViewer: React.FC<CodeViewerProps> = ({
  contentId,
  metadata,
  onError,
  onLoad,
  theme = 'light',
  showLineNumbers = true,
  wordWrap = false,
  className,
  style,
}) => {
  const muiTheme = useTheme();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [lineNumbers, setLineNumbers] = useState<boolean>(showLineNumbers);
  const [wordWrapEnabled, setWordWrapEnabled] = useState<boolean>(wordWrap);
  // Initialize selectedTheme based on UI theme mode
  const [selectedTheme, setSelectedTheme] = useState<string>(
    muiTheme.palette.mode === 'dark' ? 'dark' : theme
  );
  const [language, setLanguage] = useState<string>('plaintext');
  const [copied, setCopied] = useState<boolean>(false);

  const { contentUrl, isDirectDownload, metadata: streamingMetadata } = useDirectContent(contentId, undefined, metadata as any);

  // Update selected theme when UI theme changes
  useEffect(() => {
    setSelectedTheme(muiTheme.palette.mode === 'dark' ? 'dark' : 'light');
  }, [muiTheme.palette.mode]);

  // Load code content
  useEffect(() => {
    const loadCode = async () => {
      if (!contentUrl) return;

      setLoading(true);
      try {
        console.log('Loading code content:', contentId, 'via', isDirectDownload ? 'presigned URL' : 'streaming');

        // Fetch content from URL (works for both presigned and streaming URLs)
        const response = await fetch(contentUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const text = await response.text();
        setContent(text);
        onLoad?.();
      } catch (error: any) {
        console.error('Failed to load code:', error);
        const viewerError: ViewerError = {
          code: 'STREAMING_ERROR',
          message: 'Failed to load code content',
          details: error,
          retryable: true,
        };
        onError?.(viewerError);
      } finally {
        setLoading(false);
      }
    };

    loadCode();
  }, [contentId, contentUrl, isDirectDownload, onLoad, onError]);

  // Detect programming language from file extension
  const detectLanguage = useCallback((): string => {
    const fileName = metadata.fileName.toLowerCase();
    const extension = fileName.split('.').pop();
    
    const languageMap: Record<string, string> = {
      // JavaScript/TypeScript
      js: 'javascript',
      jsx: 'javascript',
      ts: 'typescript',
      tsx: 'typescript',
      // Python
      py: 'python',
      pyw: 'python',
      // Java/Kotlin
      java: 'java',
      kt: 'kotlin',
      // C/C++/C#
      c: 'cpp',
      cpp: 'cpp',
      cc: 'cpp',
      cxx: 'cpp',
      h: 'cpp',
      hpp: 'cpp',
      cs: 'csharp',
      // Go
      go: 'go',
      // Rust
      rs: 'rust',
      // Web
      html: 'xml',
      htm: 'xml',
      xml: 'xml',
      css: 'css',
      scss: 'scss',
      sass: 'sass',
      less: 'less',
      // Data
      json: 'json',
      yml: 'yaml',
      yaml: 'yaml',
      toml: 'toml',
      ini: 'ini',
      // Database
      sql: 'sql',
      // Shell
      sh: 'bash',
      bash: 'bash',
      zsh: 'bash',
      fish: 'bash',
      ps1: 'powershell',
      // Documentation
      md: 'markdown',
      markdown: 'markdown',
      rst: 'rest',
      // Config
      dockerfile: 'dockerfile',
      Dockerfile: 'dockerfile',
      makefile: 'makefile',
      Makefile: 'makefile',
      // Other
      rb: 'ruby',
      php: 'php',
      swift: 'swift',
      r: 'r',
      R: 'r',
      lua: 'lua',
      vim: 'vim',
    };

    const detectedLang = languageMap[extension || ''] || 'plaintext';
    return detectedLang;
  }, [metadata.fileName]);

  // Set language on load
  useEffect(() => {
    setLanguage(detectLanguage());
  }, [detectLanguage]);

  // Get syntax highlighter style
  const getHighlighterStyle = useCallback(() => {
    switch (selectedTheme) {
      case 'dark':
        return atomDark;
      case 'vs-dark':
        return vscDarkPlus;
      case 'github':
        return prism;
      case 'light':
      default:
        return vs;
    }
  }, [selectedTheme]);

  // Copy code to clipboard
  const handleCopyCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy code:', error);
    }
  }, [content]);

  // Calculate line count
  const lineCount = content.split('\n').length;

  if (loading) {
    return (
      <Box
        className={className}
        style={style}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          flexDirection: 'column',
          gap: 2,
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress color="primary" />
        <Typography variant="body2" color="text.secondary">
          Loading code...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className={className}
      style={style}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Code Viewer Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'grey.50',
          gap: 2,
        }}
      >
        {/* Left side - Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="caption"
            sx={{
              color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary'
            }}
          >
            {language.toUpperCase()} • {lineCount} lines • {content.length} chars
          </Typography>
          
          {/* Language selector */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              size="small"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="plaintext">Plain Text</MenuItem>
              <MenuItem value="javascript">JavaScript</MenuItem>
              <MenuItem value="typescript">TypeScript</MenuItem>
              <MenuItem value="python">Python</MenuItem>
              <MenuItem value="java">Java</MenuItem>
              <MenuItem value="cpp">C/C++</MenuItem>
              <MenuItem value="csharp">C#</MenuItem>
              <MenuItem value="go">Go</MenuItem>
              <MenuItem value="rust">Rust</MenuItem>
              <MenuItem value="sql">SQL</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
              <MenuItem value="xml">HTML/XML</MenuItem>
              <MenuItem value="markdown">Markdown</MenuItem>
              <MenuItem value="yaml">YAML</MenuItem>
              <MenuItem value="bash">Shell</MenuItem>
              <MenuItem value="docker">Dockerfile</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Right side - Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Theme selector */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={selectedTheme}
              onChange={(e) => setSelectedTheme(e.target.value)}
              size="small"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="light">Light</MenuItem>
              <MenuItem value="dark">Dark</MenuItem>
              <MenuItem value="github">GitHub</MenuItem>
              <MenuItem value="vs-dark">VS Dark</MenuItem>
            </Select>
          </FormControl>

          <ToggleButtonGroup
            size="small"
            sx={{
              gap: 0.5,
              '& .MuiToggleButton-root': {
                color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.7)',
                '&.Mui-selected': {
                  color: 'primary.main',
                  backgroundColor: 'action.selected',
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            }}
          >
            <Tooltip title="Line Numbers">
              <ToggleButton
                value="lineNumbers"
                selected={lineNumbers}
                onChange={() => setLineNumbers(!lineNumbers)}
              >
                <LineNumbersIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>

            <Tooltip title="Word Wrap">
              <ToggleButton
                value="wordWrap"
                selected={wordWrapEnabled}
                onChange={() => setWordWrapEnabled(!wordWrapEnabled)}
              >
                <WrapTextIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>

            <Tooltip title={copied ? "Copied!" : "Copy Code"}>
              <ToggleButton
                value="copy"
                selected={false}
                onChange={handleCopyCode}
              >
                {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Code Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          position: 'relative',
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            m: 1,
            height: 'calc(100% - 16px)',
            overflow: 'auto',
            '& pre': {
              margin: 0,
              padding: '16px !important',
              height: '100%',
              overflow: 'auto',
            },
            '& code': {
              fontFamily: "'Monaco', 'Consolas', 'Courier New', monospace !important",
              fontSize: '14px !important',
              lineHeight: '1.5 !important',
            },
          }}
        >
          <SyntaxHighlighter
            language={language}
            style={getHighlighterStyle()}
            showLineNumbers={lineNumbers}
            wrapLines={wordWrapEnabled}
            wrapLongLines={wordWrapEnabled}
            customStyle={{
              margin: 0,
              padding: '16px',
              backgroundColor: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5',
            }}
            codeTagProps={{
              style: {
                fontFamily: "'Monaco', 'Consolas', 'Courier New', monospace",
                fontSize: '14px',
              }
            }}
          >
            {content}
          </SyntaxHighlighter>
        </Paper>
      </Box>
    </Box>
  );
};

export default CodeViewer;